import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_PAGES = 5;
/** Near-lossless JPEG; backend photo path expects JPEG bytes. */
const JPEG_QUALITY = 1;
/** ~216 DPI for typical PDF pages (72dpi base × 3). */
const RENDER_SCALE = 3;

function isPdfFile(file: File): boolean {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

async function canvasToJpegInt8(canvas: HTMLCanvasElement): Promise<Int8Array> {
    const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to encode JPEG'))),
            'image/jpeg',
            JPEG_QUALITY,
        );
    });
    const buffer = await blob.arrayBuffer();
    return new Int8Array(buffer);
}

/** Renders PDF pages to a single vertical JPEG (Int8Array), matching document photo upload format. */
export async function pdfFileToJpegBytes(file: File): Promise<Int8Array> {
    const data = new Uint8Array(await file.arrayBuffer());
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    const pageCount = Math.min(pdf.numPages, MAX_PAGES);

    const pageCanvases: HTMLCanvasElement[] = [];
    let totalHeight = 0;
    let maxWidth = 0;

    for (let pageNum = 1; pageNum <= pageCount; pageNum += 1) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: RENDER_SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) throw new Error('Canvas not available');
        ctx.imageSmoothingEnabled = false;
        await page.render({ canvasContext: ctx, viewport, intent: 'print' }).promise;
        pageCanvases.push(canvas);
        totalHeight += canvas.height;
        maxWidth = Math.max(maxWidth, canvas.width);
    }

    const merged = document.createElement('canvas');
    merged.width = maxWidth;
    merged.height = totalHeight;
    const mergedCtx = merged.getContext('2d', { alpha: false });
    if (!mergedCtx) throw new Error('Canvas not available');
    mergedCtx.imageSmoothingEnabled = false;
    mergedCtx.fillStyle = '#ffffff';
    mergedCtx.fillRect(0, 0, merged.width, merged.height);

    let y = 0;
    for (const pageCanvas of pageCanvases) {
        mergedCtx.drawImage(pageCanvas, 0, y);
        y += pageCanvas.height;
    }

    return canvasToJpegInt8(merged);
}

export async function fileToDocumentPhotoBytes(file: File): Promise<Int8Array> {
    if (isPdfFile(file)) {
        return pdfFileToJpegBytes(file);
    }
    const buffer = await file.arrayBuffer();
    return new Int8Array(buffer);
}

export { isPdfFile };
